import { useLingui } from '@lingui/react/macro'
import { useEffect, useState, type ReactNode } from 'react'
import { useRouteError } from 'react-router'

import { LoadError } from '@/shared/components/LoadError'
import { LoadingOranje } from '@/shared/components/LoadingOranje'
import { isStaleChunkError, reloadOnceForNewVersion } from '@/shared/lib/staleDeploy'

/**
 * La pantalla de error de todas las rutas, en lugar de la de React Router
 * («Unexpected Application Error!»). El caso de siempre es un despliegue
 * nuevo con la pestaña abierta: se recarga sola y no se ve el error (Hugo,
 * 2026-10-28). Cualquier otro error ofrece volver a cargar.
 */
export function RouteErrorPage(): ReactNode {
  const { t } = useLingui()
  const error = useRouteError()
  const isNewVersion = isStaleChunkError(error)
  const [reloading, setReloading] = useState(isNewVersion)

  useEffect(() => {
    if (isNewVersion && !reloadOnceForNewVersion()) setReloading(false)
  }, [isNewVersion])

  if (reloading) return <LoadingOranje label={t`Hay una versión nueva de Oranje. Actualizando…`} />

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-2 p-4">
      <div className="w-full max-w-md">
        <LoadError
          message={
            isNewVersion
              ? t`No se pudo abrir la versión nueva de Oranje. Vuelve a cargar la página; si sigue igual, cierra la pestaña y ábrela de nuevo.`
              : t`Algo salió mal al abrir esta pantalla. Vuelve a cargarla para intentarlo de nuevo.`
          }
          onRetry={() => {
            window.location.reload()
          }}
        />
      </div>
    </main>
  )
}
